/**
 * Shared utilities for bank account draft rows in the setup flow.
 * Used by setupPage.js and performanceOnboardingBanner.js.
 */

export const draftBankAccountsStorageKey = (customerId) => {
  if (!customerId) return null;
  return `draft_bank_accounts_${customerId}`;
};

export const normalizeDraftBankAccountRows = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  return list.map((row, idx) => {
    const rawTypeKey = typeof row?.typeKey === "string" ? row.typeKey : "";
    const typeKey =
      rawTypeKey === "checking" || rawTypeKey === "savings" || rawTypeKey === "credit_card"
        ? rawTypeKey
        : "checking";

    const bankName = typeof row?.bankName === "string" ? row.bankName : "";
    const accountName = typeof row?.accountName === "string" ? row.accountName : "";
    const id =
      typeof row?.id === "string" && row.id.trim()
        ? row.id
        : `draft-${Date.now()}-${idx}-${Math.random().toString(16).slice(2)}`;

    return {
      id,
      bankName,
      accountName,
      typeKey,
      isConnected: false,
      connectionMethod: null,
    };
  });
};

export const loadDraftBankAccountRows = (customerId) => {
  const key = draftBankAccountsStorageKey(customerId);
  if (!key) return [];

  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return normalizeDraftBankAccountRows(parsed);
  } catch (err) {
    console.warn("Failed reading saved bank account drafts:", err);
    return [];
  }
};

export const saveDraftBankAccountRows = (customerId, rows) => {
  const key = draftBankAccountsStorageKey(customerId);
  if (!key) return;

  try {
    localStorage.setItem(key, JSON.stringify(rows || []));
  } catch (err) {
    console.warn("Failed saving bank account drafts:", err);
  }
};

export const buildDraftRowId = (prefix, idx) => {
  const rand =
    typeof crypto !== "undefined" && crypto?.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(16).slice(2);
  return `${prefix}-${idx}-${Date.now()}-${rand}`;
};

/**
 * Build an array of draft account rows from counts (checking, savings, credit).
 */
export const buildDraftAccountSetupRows = (counts) => {
  const checkingCount = Math.max(0, Number(counts?.checking || 0));
  const savingsCount = Math.max(0, Number(counts?.savings || 0));
  const creditCount = Math.max(0, Number(counts?.credit || 0));

  const rows = [];

  for (let i = 1; i <= checkingCount; i += 1) {
    rows.push({
      id: buildDraftRowId("checking", i),
      bankName: "",
      typeKey: "checking",
      accountName: "",
      isConnected: false,
      connectionMethod: null,
    });
  }

  for (let i = 1; i <= savingsCount; i += 1) {
    rows.push({
      id: buildDraftRowId("savings", i),
      bankName: "",
      typeKey: "savings",
      accountName: "",
      isConnected: false,
      connectionMethod: null,
    });
  }

  for (let i = 1; i <= creditCount; i += 1) {
    rows.push({
      id: buildDraftRowId("credit", i),
      bankName: "",
      typeKey: "credit_card",
      accountName: "",
      isConnected: false,
      connectionMethod: null,
    });
  }

  return rows;
};
